import { useEffect } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';

import AdminStats from '@/views/admin/AdminStats.tsx';
import CreateClubOnboarding from '@/views/auth/CreateClubOnboarding.tsx';
import CreateOrgOnboarding from '@/views/auth/CreateOrgOnboarding.tsx';
import ResetPassword from '@/views/auth/ResetPassword.tsx';
import VerifyEmail from '@/views/auth/VerifyEmail.tsx';
import OrganizerProfileEdit from '@/views/organizer/OrganizerProfileEdit.tsx';
import { Role } from '@shared';

import { ErrorBoundary } from './components/ErrorBoundary';
import FeedbackWidget from './components/feedback/FeedbackWidget';
import { ROUTES } from './config/routes';
import { useHydration } from './hooks/useHydration';
import { getSaasParams } from './lib/api/saasApi';
import { RoleGuard } from './lib/auth/RoleGuard';
import { useAuthStore } from './stores/auth-store';
import { useSaasStore } from './stores/saas-store';
import AdminBlog from './views/admin/AdminBlog';
import AdminBlogCreate from './views/admin/AdminBlogCreate';
import AdminBlogEdit from './views/admin/AdminBlogEdit';
import AdminClubs from './views/admin/AdminClubs';
import AdminCompetitions from './views/admin/AdminCompetitions';
import AdminDashboard from './views/admin/AdminDashboard';
import AdminFeedbackList from './views/admin/AdminFeedbackList';
// Super Admin
import AdminGdprBreaches from './views/admin/AdminGdprBreaches';
import AdminGdprTreatments from './views/admin/AdminGdprTreatments';
import AdminOrganizers from './views/admin/AdminOrganizers';
import AdminPayments from './views/admin/AdminPayments';
import AdminQueues from './views/admin/AdminQueues';
import AdminRefundRequests from './views/admin/AdminRefundRequests';
import AdminSettings from './views/admin/AdminSettings';
import AdminUserEdit from './views/admin/AdminUserEdit';
import AdminUsers from './views/admin/AdminUsers';
// Auth
import ForgotPassword from './views/auth/ForgotPassword';
import Login from './views/auth/Login';
import OAuthCallback from './views/auth/OAuthCallback';
import OAuthOnboarding from './views/auth/OAuthOnboarding';
import Register from './views/auth/Register';
import ClubAnalytics from './views/club/ClubAnalytics';
// Club
import ClubBlog from './views/club/ClubBlog';
import ClubBlogCreate from './views/club/ClubBlogCreate';
import ClubBlogEdit from './views/club/ClubBlogEdit';
import ClubCompetitions from './views/club/ClubCompetitions';
import ClubDashboard from './views/club/ClubDashboard';
import ClubHistory from './views/club/ClubHistory';
import ClubInscriptions from './views/club/ClubInscriptions';
import ClubMembers from './views/club/ClubMembers';
import ClubOrganizers from './views/club/ClubOrganizers';
import ClubProfileEdit from './views/club/ClubProfileEdit';
import ClubSettings from './views/club/ClubSettings';
import ClubTeams from './views/club/ClubTeams';
// Common
import RoleLanding from './views/common/RoleLanding';
import CompetitionCreate from './views/organizer/CompetitionCreate';
import CompetitionEdit from './views/organizer/CompetitionEdit';
import CompetitionList from './views/organizer/CompetitionList';
import CompetitionResultsEntry from './views/organizer/CompetitionResultsEntry';
// Organisateur
import OrganizerAdminRequests from './views/organizer/OrganizerAdminRequests';
import OrganizerAnalytics from './views/organizer/OrganizerAnalytics';
import OrganizerBlog from './views/organizer/OrganizerBlog';
import OrganizerBlogCreate from './views/organizer/OrganizerBlogCreate';
import OrganizerBlogEdit from './views/organizer/OrganizerBlogEdit';
import OrganizerClubs from './views/organizer/OrganizerClubs';
import OrganizerDashboard from './views/organizer/OrganizerDashboard';
import OrganizerInscriptions from './views/organizer/OrganizerInscriptions';
import OrganizerPayments from './views/organizer/OrganizerPayments';
import OrganizerRefundRequests from './views/organizer/OrganizerRefundRequests';
import OrganizerSettings from './views/organizer/OrganizerSettings';
// Joueur
import PlayerClub from './views/player/PlayerClub';
import PlayerDashboard from './views/player/PlayerDashboard';
import PlayerHistory from './views/player/PlayerHistory';
import PlayerInscriptions from './views/player/PlayerInscriptions';
import PlayerProfile from './views/player/PlayerProfile';
import PlayerSettings from './views/player/PlayerSettings.tsx';
import PlayerStats from './views/player/PlayerStats';
// Profile
import ProfileSecurity from './views/profile/ProfileSecurity';
import SiteCmsLanding from './views/site-cms/SiteCmsLanding';

// Synchronise la session après réhydratation du localStorage.
// - Authentifié localement : refresh PROACTIF de l'access token (via le cookie
//   refresh token) puis rafraîchit les données user. Indispensable car l'access
//   token persité peut être expiré : sur une route @Public (ex. GET /competitions),
//   un token expiré n'est pas rejeté (pas de 401, donc pas de refresh auto) et le
//   serveur répond en vue publique → les concours DISABLED_BY_ADMIN (et tout
//   contexte owner) disparaîtraient silencieusement de l'admin. On garantit donc
//   un token frais dès le montage. tryRestoreSession est non-destructif en cas
//   d'échec (cookie mort) : la session locale est conservée jusqu'au prochain 401.
// - Non authentifié : tente une restauration silencieuse via le cookie refresh
//   token (permet le SSO entre le backoffice admin et le front public).
function SessionSync() {
  const hydrated = useHydration();
  const tryRestoreSession = useAuthStore(s => s.tryRestoreSession);

  useEffect(() => {
    if (!hydrated) return;
    void tryRestoreSession();
  }, [hydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// Fetches SaaS params once on mount and populates the saas-store.
function SaasParamsSync() {
  const setParams = useSaasStore(s => s.setParams);

  useEffect(() => {
    getSaasParams()
      .then(params => setParams(params))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function AppFrame() {
  return (
    <>
      <SessionSync />
      <SaasParamsSync />
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
      <FeedbackWidget />
    </>
  );
}

const appRouter = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<AppFrame />}>
      {/* Auth */}
      <Route path={ROUTES.LOGIN} element={<Login />} />
      <Route path={ROUTES.REGISTER} element={<Register />} />
      <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
      <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
      <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmail />} />
      <Route path={ROUTES.AUTH_CALLBACK} element={<OAuthCallback />} />
      <Route path={ROUTES.OAUTH_ONBOARDING} element={<OAuthOnboarding />} />

      {/* Onboarding post-inscription */}
      <Route path={ROUTES.CREATE_CLUB_ONBOARDING} element={<CreateClubOnboarding />} />
      <Route path={ROUTES.CREATE_ORG_ONBOARDING} element={<CreateOrgOnboarding />} />

      {/* Page racine : redirection vers le bon dashboard selon le role */}
      <Route path={ROUTES.ROOT} element={<RoleLanding />} />

      {/* Super Admin */}
      <Route
        path={ROUTES.ADMIN_DASHBOARD}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminDashboard />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ADMIN_USERS}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminUsers />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ADMIN_USER_EDIT}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminUserEdit />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ADMIN_CLUBS}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminClubs />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ADMIN_ORGANIZERS}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminOrganizers />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ADMIN_COMPETITIONS}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminCompetitions />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ADMIN_PAYMENTS}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminPayments />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ADMIN_REFUND_REQUESTS}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminRefundRequests />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ADMIN_FEEDBACK}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminFeedbackList />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ADMIN_ANALYTICS}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminStats />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ADMIN_BLOG}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminBlog />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ADMIN_BLOG_CREATE}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminBlogCreate />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ADMIN_BLOG_EDIT}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminBlogEdit />
          </RoleGuard>
        }
      />

      <Route
        path={ROUTES.ADMIN_GDPR_TREATMENTS}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminGdprTreatments />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ADMIN_GDPR_BREACHES}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminGdprBreaches />
          </RoleGuard>
        }
      />

      {/* Bull Board (monitoring queues) — dev/test uniquement, absent du build prod */}
      {import.meta.env.DEV && (
        <Route
          path={ROUTES.ADMIN_QUEUES}
          element={
            <RoleGuard roles={Role.SUPER_ADMIN}>
              <AdminQueues />
            </RoleGuard>
          }
        />
      )}

      <Route path={ROUTES.ADMIN_SAAS_PARAMS} element={<Navigate to={`${ROUTES.ADMIN_SETTINGS}?tab=saas`} replace />} />
      <Route
        path={ROUTES.ADMIN_SETTINGS}
        element={
          <RoleGuard roles={Role.SUPER_ADMIN}>
            <AdminSettings />
          </RoleGuard>
        }
      />

      {/* CMS S1: visible to eligible roles even when the offer is locked. */}
      <Route
        path={ROUTES.ORGANIZER_SITE_CMS}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <SiteCmsLanding />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.CLUB_SITE_CMS}
        element={
          <RoleGuard roles={Role.CLUB_ADMIN}>
            <SiteCmsLanding />
          </RoleGuard>
        }
      />

      {/* Organisateur */}
      <Route
        path={ROUTES.ORGANIZER_DASHBOARD}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <OrganizerDashboard />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.COMPETITION_LIST}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <CompetitionList />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.COMPETITION_CREATE}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <CompetitionCreate />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.COMPETITION_EDIT}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <CompetitionEdit />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.COMPETITION_RESULTS_ENTRY}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <CompetitionResultsEntry />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ORGANIZER_INSCRIPTIONS}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <OrganizerInscriptions />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ORGANIZER_CLUBS}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <OrganizerClubs />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ORGANIZER_PAYMENTS}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <OrganizerPayments />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ORGANIZER_REFUND_REQUESTS}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <OrganizerRefundRequests />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ORGANIZER_ANALYTICS}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <OrganizerAnalytics />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ORGANIZER_PROFILE_EDIT}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <OrganizerProfileEdit />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ORGANIZER_SETTINGS}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <OrganizerSettings />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ORGANIZER_ADMIN_REQUESTS}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <OrganizerAdminRequests />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ORGANIZER_BLOG}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <OrganizerBlog />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ORGANIZER_BLOG_CREATE}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <OrganizerBlogCreate />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.ORGANIZER_BLOG_EDIT}
        element={
          <RoleGuard roles={Role.ORGANIZATION_ADMIN}>
            <OrganizerBlogEdit />
          </RoleGuard>
        }
      />

      {/* Club */}
      <Route
        path={ROUTES.CLUB_DASHBOARD}
        element={
          <RoleGuard roles={Role.CLUB_ADMIN}>
            <ClubDashboard />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.CLUB_MEMBERS}
        element={
          <RoleGuard roles={Role.CLUB_ADMIN}>
            <ClubMembers />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.CLUB_TEAMS}
        element={
          <RoleGuard roles={Role.CLUB_ADMIN}>
            <ClubTeams />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.CLUB_COMPETITIONS}
        element={
          <RoleGuard roles={Role.CLUB_ADMIN}>
            <ClubCompetitions />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.CLUB_INSCRIPTIONS}
        element={
          <RoleGuard roles={Role.CLUB_ADMIN}>
            <ClubInscriptions />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.CLUB_HISTORY}
        element={
          <RoleGuard roles={Role.CLUB_ADMIN}>
            <ClubHistory />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.CLUB_ORGANIZERS}
        element={
          <RoleGuard roles={Role.CLUB_ADMIN}>
            <ClubOrganizers />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.CLUB_ANALYTICS}
        element={
          <RoleGuard roles={Role.CLUB_ADMIN}>
            <ClubAnalytics />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.CLUB_PROFILE_EDIT}
        element={
          <RoleGuard roles={Role.CLUB_ADMIN}>
            <ClubProfileEdit />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.CLUB_SETTINGS}
        element={
          <RoleGuard roles={Role.CLUB_ADMIN}>
            <ClubSettings />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.CLUB_BLOG}
        element={
          <RoleGuard roles={Role.CLUB_ADMIN}>
            <ClubBlog />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.CLUB_BLOG_CREATE}
        element={
          <RoleGuard roles={Role.CLUB_ADMIN}>
            <ClubBlogCreate />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.CLUB_BLOG_EDIT}
        element={
          <RoleGuard roles={Role.CLUB_ADMIN}>
            <ClubBlogEdit />
          </RoleGuard>
        }
      />

      {/* Joueur */}
      <Route
        path={ROUTES.PLAYER_DASHBOARD}
        element={
          <RoleGuard roles={Role.MEMBER}>
            <PlayerDashboard />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.PLAYER_INSCRIPTIONS}
        element={
          <RoleGuard roles={Role.MEMBER}>
            <PlayerInscriptions />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.PLAYER_CLUB}
        element={
          <RoleGuard roles={Role.MEMBER}>
            <PlayerClub />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.PLAYER_HISTORY}
        element={
          <RoleGuard roles={Role.MEMBER}>
            <PlayerHistory />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.PLAYER_STATS}
        element={
          <RoleGuard roles={Role.MEMBER}>
            <PlayerStats />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.PLAYER_PROFILE}
        element={
          <RoleGuard roles={Role.MEMBER}>
            <PlayerProfile />
          </RoleGuard>
        }
      />
      <Route
        path={ROUTES.PLAYER_PREFERENCES}
        element={
          <RoleGuard roles={Role.MEMBER}>
            <PlayerSettings />
          </RoleGuard>
        }
      />

      {/* Profil / Sécurité — accessible à tous les rôles authentifiés */}
      <Route
        path={ROUTES.PROFILE_SECURITY}
        element={
          <RoleGuard roles={[Role.SUPER_ADMIN, Role.ORGANIZATION_ADMIN, Role.CLUB_ADMIN, Role.MEMBER]}>
            <ProfileSecurity />
          </RoleGuard>
        }
      />

      {/* 404 */}
      <Route path='*' element={<Navigate to={ROUTES.ROOT} replace />} />
    </Route>,
  ),
);

function App() {
  return <RouterProvider router={appRouter} />;
}

export default App;
